import { FC, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Alert,
} from "@mui/material";
import { Save, Cancel } from "@mui/icons-material";
import ControlledTextField from "../../components/controlledComponents/ControlledTextField";
import ControlledSelectField from "../../components/controlledComponents/ControlledSelectField";
import { TSubject } from "../../../apis/academic/academic.interfaces";
import {
  useCreateSubjectHook,
  useUpdateSubjectHook,
} from "../../../hooks/useAcademicHook";
import { logger } from "../../../utils/common";

const subjectSchema = z.object({
  name: z
    .string()
    .min(1, "Name is required")
    .max(200, "Name must not exceed 200 characters"),
  code: z
    .string()
    .min(1, "Code is required")
    .max(50, "Code must not exceed 50 characters"),
  description: z
    .string()
    .max(1000, "Description must not exceed 1000 characters")
    .optional()
    .or(z.literal("")),
  subjectType: z
    .string()
    .max(100)
    .optional()
    .or(z.literal("")),
  isElective: z.enum(["yes", "no"]),
});

type SubjectFormValues = z.infer<typeof subjectSchema>;

interface ManageSubjectDialogProps {
  open: boolean;
  onClose: () => void;
  subject?: TSubject | null;
}

const ManageSubjectDialog: FC<ManageSubjectDialogProps> = ({
  open,
  onClose,
  subject,
}) => {
  const createMutation = useCreateSubjectHook();
  const updateMutation = useUpdateSubjectHook();
  const isEditMode = !!subject;

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<SubjectFormValues>({
    resolver: zodResolver(subjectSchema),
    defaultValues: {
      name: "",
      code: "",
      description: "",
      subjectType: "",
      isElective: "no",
    },
  });

  useEffect(() => {
    if (!open) return;
    if (subject) {
      reset({
        name: subject.name,
        code: subject.code,
        description: subject.description ?? "",
        subjectType: subject.subjectType ?? "",
        isElective: subject.isElective ? "yes" : "no",
      });
    } else {
      reset({
        name: "",
        code: "",
        description: "",
        subjectType: "",
        isElective: "no",
      });
    }
  }, [open, subject, reset]);

  const onSubmit = async (data: SubjectFormValues) => {
    try {
      const isElective = data.isElective === "yes";
      if (isEditMode && subject) {
        await updateMutation.mutateAsync({
          id: subject.id,
          request: {
            name: data.name,
            code: data.code,
            description: data.description || undefined,
            subjectType: data.subjectType || undefined,
            isElective,
          },
        });
        logger.show_success("Subject updated.");
      } else {
        await createMutation.mutateAsync({
          name: data.name,
          code: data.code,
          description: data.description || undefined,
          subjectType: data.subjectType || undefined,
          isElective,
        });
        logger.show_success("Subject created.");
      }
      onClose();
    } catch (e) {
      logger.show_error(
        e instanceof Error ? e.message : "Could not save subject.",
      );
    }
  };

  const pending =
    isSubmitting || createMutation.isPending || updateMutation.isPending;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      data-testid="manage-subject-dialog"
    >
      <DialogTitle data-testid="manage-subject-dialog-title">
        {isEditMode ? "Edit subject" : "Add subject"}
      </DialogTitle>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent data-testid="manage-subject-dialog-content">
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <ControlledTextField
              control={control as never}
              name="name"
              id="subject-name"
              label="Name"
              required
              errorMessage={errors.name?.message}
              inputProps={{ "data-testid": "subject-name-input" }}
            />
            <ControlledTextField
              control={control as never}
              name="code"
              id="subject-code"
              label="Code"
              required
              errorMessage={errors.code?.message}
              inputProps={{ "data-testid": "subject-code-input" }}
            />
            <ControlledTextField
              control={control as never}
              name="subjectType"
              id="subject-type"
              label="Type (optional)"
              errorMessage={errors.subjectType?.message}
              inputProps={{ "data-testid": "subject-type-input" }}
            />
            <ControlledSelectField
              control={control as never}
              name="isElective"
              id="subject-elective"
              label="Elective"
              itemList={[
                { key: "no", value: "No" },
                { key: "yes", value: "Yes" },
              ]}
              isRequired
              errorMessage={errors.isElective?.message}
            />
            <Grid item xs={12}>
              <ControlledTextField
                control={control as never}
                name="description"
                id="subject-description"
                label="Description"
                multiline
                minRows={2}
                errorMessage={errors.description?.message}
                inputProps={{ "data-testid": "subject-description-input" }}
                isGridItem={false}
                fullWidth
              />
            </Grid>
          </Grid>
          {(createMutation.isError || updateMutation.isError) && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {createMutation.error?.message ||
                updateMutation.error?.message ||
                "Save failed."}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            startIcon={<Cancel />}
            onClick={onClose}
            disabled={pending}
            data-testid="manage-subject-cancel"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            startIcon={<Save />}
            disabled={pending}
            data-testid="manage-subject-save"
          >
            Save
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default ManageSubjectDialog;
